import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { useAccount } from "../context/AccountContext";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import "./PartialDispatchMap.css";

const PartialDispatchMap = () => {
  const { currentAccount } = useAccount();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [transporterProfiles, setTransporterProfiles] = useState({});

  useEffect(() => {
    if (!selectedOrder) return;

    const updated = orders.find(o => o.id === selectedOrder.id);
    if (updated) {
      setSelectedOrder(updated); // 🔥 refresh selected order with new data
    }
  }, [orders]);

 // Fetch Transporter Profiles
  useEffect(() => {
    const db = getDatabase();
    const profilesRef = ref(db, "transporter-profile");

    const unsub = onValue(profilesRef, (snapshot) => {
      if (snapshot.exists()) {
        setTransporterProfiles(snapshot.val());
      }
    });

    return () => unsub();
  }, []);

  // Fetch Orders (Real-time)
  useEffect(() => {
    if (!currentAccount?.uid) return;

    const db = getDatabase();
    const ordersRef = ref(db, "transport_orders");

    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setOrders([]);
        return;
      }

      const list = [];

      Object.entries(data).forEach(([transporterId, transporterOrders]) => {
        Object.entries(transporterOrders || {}).forEach(([id, order]) => {
          const isMyOrder =
            order.clientUid === currentAccount.uid ||
            order.clientId === currentAccount.uid ||
            order.companyName === currentAccount.company_name;

          const isPartial = (order.status || "").toLowerCase() === "partial dispatched";

          if (isMyOrder && isPartial) {
            const profile = transporterProfiles[transporterId] || {};
            list.push({
              id,
              transporterId,
              transporterName: profile.company || "Unknown Transporter",
              transporterPhone: profile.phone || "N/A",
              ...order,
            });
          }
        });
      });
      

      const sortedList = list.sort((a, b) => {
        const aLive = a?.dispatch?.items?.some(i => i?.location?.sharing === true);
        const bLive = b?.dispatch?.items?.some(i => i?.location?.sharing === true);

        if (aLive && !bLive) return -1;
        if (!aLive && bLive) return 1;

        return (b.orderCreatedAt || b.createdAt || 0) - (a.orderCreatedAt || a.createdAt || 0);
      });

      setOrders(sortedList);
    });

    return () => unsubscribe();
  }, [currentAccount?.uid, currentAccount?.company_name, transporterProfiles]);

  return (
    <div className="pd-container">
      {/* LEFT SIDE - Orders List */}
      <div className="track-pd-left">
        <h2>Dispatched Orders</h2>

        {orders.length === 0 ? (
          <p>No partial dispatch orders</p>
        ) : (
          orders.map((order) => {
            const isLive = order?.dispatch?.items?.some(
              (i) => i?.location?.sharing === true
            );

            return (
              <div
                key={order.id}
                className={`track-pd-card ${selectedOrder?.id === order.id ? "active" : ""} ${isLive ? "live" : "offline"
                  }`}
                onClick={() => setSelectedOrder(order)}
              >
                <div className="track-card-header">
                  <h5>{order.orderId}</h5>
                  <span className={`badge ${isLive ? "on" : "off"}`}>
                    {isLive ? "LIVE" : "OFF"}
                  </span>
                </div>
                <p><strong>Transporter:</strong> {order.transporterName}</p>
                <p><strong>Route:</strong> {order.from} → {order.to}</p>
             <p>
  <strong>Truck:</strong>{" "}
  {order.dispatch?.items?.[0]?.truckNumber ||
   order.truckNumber ||
   "N/A"}
</p>
                <p><strong>Qyantity:</strong> {order.quantity} {order.quantityUnit}</p>
              </div>
            );
          })
        )}
      </div>

      {/* RIGHT SIDE - Live Tracking */}
      <div className="pd-right">
        {selectedOrder ? (
         <LiveTracking key={selectedOrder.id} order={selectedOrder} />
        ) : (
          <div className="pd-placeholder">
      <div className="placeholder-card">
        <h3>🚛 Live Tracking</h3>
        <p>Select an order from the sidebar to view live tracking.</p>
      </div>
    </div>
        )}
      </div>
    </div>
  );
};

function LiveTracking({ order }) {
  const [address, setAddress] = React.useState("");
  const [pulseScale, setPulseScale] = useState(1);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  
  const mapRef = React.useRef(null);
  const timeoutRef = React.useRef(null);
  const growingRef = useRef(true);

  const items = order?.dispatch?.items || [];

 const liveItem = useMemo(() => {
  return (
    items.find(i => i?.location?.sharing === true) ||
    items.find(i => i?.location) ||
    items[0]
  );
}, [items]);

  const location = liveItem?.location;
  const isLive = !!location?.sharing;

  const isMapReady = isLoaded && typeof window !== "undefined" && !!window.google;

  // Pulse animation for glow
  useEffect(() => {
    if (!isLive) {
      setPulseScale(1);
      return;
    }

    const interval = setInterval(() => {
      setPulseScale((prev) => {
        if (prev >= 1.35) growingRef.current = false;
        if (prev <= 1) growingRef.current = true;
        return growingRef.current ? prev + 0.035 : prev - 0.035;
      });
    }, 120);

    return () => clearInterval(interval);
  }, [isLive]);

  // Pan to location
  React.useEffect(() => {
    if (!isMapReady || !mapRef.current || !location?.lat || !location?.lng) return;
    mapRef.current.panTo({ lat: location.lat, lng: location.lng });
    mapRef.current.setZoom(16);
  }, [location?.lat, location?.lng, isMapReady]);

  // Geocoding
  useEffect(() => {
    if (!isMapReady || !location?.lat || !location?.lng) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: location.lat, lng: location.lng } },
        (results, status) => {
          if (status === "OK" && results?.[0]) {
            setAddress(results[0].formatted_address);
          }
        }
      );
    }, 1200);
  }, [location?.lat, location?.lng, isMapReady]);

  // ✅ Truck Icon Memo (NOW BEFORE early return)
  const truckIcon = React.useMemo(() => {
    if (!window.google) return null;

    const color = isLive ? "#1E88E5" : "#9E9E9E";

   const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
  ${isLive ? `
    <circle cx="12" cy="12" r="${8 * pulseScale}" fill="none" stroke="#1E88E5" stroke-width="1.8" opacity="0.35"/>
    <circle cx="12" cy="12" r="${11.5 * pulseScale}" fill="none" stroke="#1E88E5" stroke-width="1.2" opacity="0.18"/>
  ` : ''}

  <!-- Truck body -->
  <rect
    x="1"
    y="3"
    width="15"
    height="13"
    rx="1"
    fill="${color}"
    stroke="${color}"
    stroke-width="1"
  />

  <!-- Truck cabin -->
  <polygon
    points="16 8 20 8 23 11 23 16 16 16"
    fill="${color}"
    stroke="${color}"
    stroke-width="1"
  />

  <!-- Wheels -->
  <circle cx="5.5" cy="18.5" r="2.5" fill="#222" />
  <circle cx="18.5" cy="18.5" r="2.5" fill="#222" />

  <!-- Wheel centers -->
  <circle cx="5.5" cy="18.5" r="1" fill="#fff" />
  <circle cx="18.5" cy="18.5" r="1" fill="#fff" />

  ${isLive ? `<circle cx="12" cy="12" r="2" fill="#fff"/>` : ""}
</svg>`;

    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 16),
    };
  }, [isLive, pulseScale]);

  if (!isMapReady || !location?.lat || !location?.lng) {
  return <p>Loading map...</p>;
}

  return (
    <div className="tracking-container">
      <div className="tracking-header">
        <h3>Live Tracking - {order.orderId}</h3>
        <span className={`status-badge ${isLive ? "on" : "off"}`}>
          {isLive ? "● LIVE" : "LOCATION OFF"}
        </span>
      </div>

      <div className="live-address text-primary mb-4">
        📍 {address || "Fetching address..."}
      </div>

     <div className="info-grid">
 

  <div className="info-card">
    <span className="label">Transporter</span>
    <span className="value">{order.transporterName}</span>
  </div>

  <div className="info-card">
    <span className="label">Transporter Phone</span>
    <span className="value">{order.transporterPhone}</span>
  </div>

  <div className="info-card">
    <span className="label">Driver Name</span>
    <span className="value">{liveItem?.driverName || "N/A"}</span>
  </div>

  <div className="info-card">
    <span className="label">Driver Phone</span>
    <span className="value">{liveItem?.driverNumber || "N/A"}</span>
  </div>

  <div className="info-card">
    <span className="label">Truck Number</span>
    <span className="value">{liveItem?.truckNumber || "N/A"}</span>
  </div>

   <div className="info-card">
    <span className="label">🚀 Speed</span>
    <span className="value">
      {location?.speed ? location.speed.toFixed(1) : 0} km/h
    </span>
  </div>
  
</div>
    
    <div className="map-wrapper">
  <GoogleMap
    onLoad={(map) => (mapRef.current = map)}
    zoom={15}
    center={{
      lat: location.lat,
      lng: location.lng,
    }}
    mapContainerStyle={{
      width: "100%",
      height: "460px",
    }}
  >
    {window.google && truckIcon && (
      <Marker
        key={`${location.lat}-${location.lng}-${isLive}`}
        position={{
          lat: location.lat,
          lng: location.lng,
        }}
        icon={truckIcon}
      />
    )}
  </GoogleMap>
</div>
    </div>
  );
}
export default PartialDispatchMap;